<template>
  <div class="payment-reports-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.payment_reports.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.payment_reports.subtitle') }}</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <select v-model="filters.provider" class="filter-select" @change="fetchReport">
        <option value="">{{ $t('admin.payment_reports.filter_all_providers') }}</option>
        <option value="card">{{ $t('admin.payment_reports.filter_card') }}</option>
        <option value="crypto">{{ $t('admin.payment_reports.filter_crypto') }}</option>
        <option value="skrill">{{ $t('admin.payment_reports.filter_skrill') }}</option>
        <option value="neteller">{{ $t('admin.payment_reports.filter_neteller') }}</option>
        <option value="sepa">{{ $t('admin.payment_reports.filter_sepa') }}</option>
        <option value="ach">{{ $t('admin.payment_reports.filter_ach') }}</option>
        <option value="manual">{{ $t('admin.payment_reports.filter_manual') }}</option>
      </select>

      <input
        v-model="filters.start_date"
        type="date"
        class="filter-input"
        @change="fetchReport"
      >
      <input
        v-model="filters.end_date"
        type="date"
        class="filter-input"
        @change="fetchReport"
      >

      <button class="btn btn-secondary" @click="clearFilters">{{ $t('admin.payment_reports.filter_clear') }}</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="table-loading">
      <span class="spinner"></span>
      {{ $t('admin.payment_reports.loading') }}
    </div>

    <!-- Empty State -->
    <div v-else-if="providers.length === 0" class="empty-state">
      {{ $t('admin.payment_reports.no_data') }}
    </div>

    <template v-else>
      <!-- Summary Cards -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">{{ $t('admin.payment_reports.stat_total_volume') }}</div>
          <div class="stat-value stat-positive">{{ formatCents(totalVolume) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">{{ $t('admin.payment_reports.stat_total_deposits') }}</div>
          <div class="stat-value">{{ totalDeposits.toLocaleString() }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">{{ $t('admin.payment_reports.stat_total_withdrawals') }}</div>
          <div class="stat-value">{{ totalWithdrawals.toLocaleString() }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">{{ $t('admin.payment_reports.stat_avg_success_rate') }}</div>
          <div class="stat-value" :class="avgSuccessRate >= 85 ? 'stat-positive' : 'stat-negative'">
            {{ avgSuccessRate.toFixed(1) }}%
          </div>
        </div>
      </div>

      <!-- Provider Table -->
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>{{ $t('admin.payment_reports.col_provider') }}</th>
              <th>{{ $t('admin.payment_reports.col_deposits') }}</th>
              <th>{{ $t('admin.payment_reports.col_withdrawals') }}</th>
              <th>{{ $t('admin.payment_reports.col_volume') }}</th>
              <th>{{ $t('admin.payment_reports.col_success_rate') }}</th>
              <th>{{ $t('admin.payment_reports.col_avg_processing_time') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in providers" :key="p.payment_method">
              <td>
                <span class="provider-name">{{ formatMethod(p.payment_method) }}</span>
              </td>
              <td>
                <span class="count-value">{{ p.total_deposits.toLocaleString() }}</span>
              </td>
              <td>
                <span class="count-value">{{ p.total_withdrawals.toLocaleString() }}</span>
              </td>
              <td>
                <span class="amount-value amount-positive">{{ formatCents(p.total_volume_cents) }}</span>
              </td>
              <td>
                <span
                  class="success-rate"
                  :class="successRateClass(p.success_rate)"
                >{{ p.success_rate }}%</span>
              </td>
              <td>
                <span v-if="p.avg_processing_time_seconds !== null" class="processing-time">
                  {{ formatDuration(p.avg_processing_time_seconds) }}
                </span>
                <span v-else class="text-muted">&mdash;</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

interface ProviderReport {
  payment_method: string
  total_deposits: number
  total_withdrawals: number
  total_volume_cents: number
  success_rate: number
  avg_processing_time_seconds: number | null
}

const { t } = useI18n()
const toast = useToastStore()

const loading = ref(false)
const providers = ref<ProviderReport[]>([])
const filters = ref({
  provider: '',
  start_date: '',
  end_date: '',
})

const totalVolume = computed(() => providers.value.reduce((sum, p) => sum + p.total_volume_cents, 0))
const totalDeposits = computed(() => providers.value.reduce((sum, p) => sum + p.total_deposits, 0))
const totalWithdrawals = computed(() => providers.value.reduce((sum, p) => sum + p.total_withdrawals, 0))
const avgSuccessRate = computed(() => {
  if (providers.value.length === 0) return 0
  const sum = providers.value.reduce((s, p) => s + p.success_rate, 0)
  return sum / providers.value.length
})

function formatCents(cents: number): string {
  const dollars = cents / 100
  const sign = dollars < 0 ? '-' : ''
  return sign + '$' + Math.abs(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatMethod(method: string): string {
  const names: Record<string, string> = {
    card: 'Card',
    crypto: 'Crypto',
    skrill: 'Skrill',
    neteller: 'Neteller',
    sepa: 'SEPA',
    ach: 'ACH',
    manual: 'Manual',
  }
  return names[method] ?? method.charAt(0).toUpperCase() + method.slice(1)
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`
  return `${(seconds / 86400).toFixed(1)}d`
}

function successRateClass(rate: number): string {
  if (rate >= 95) return 'rate-green'
  if (rate >= 85) return 'rate-yellow'
  return 'rate-red'
}

function clearFilters() {
  filters.value = { provider: '', start_date: '', end_date: '' }
  fetchReport()
}

async function fetchReport() {
  loading.value = true
  try {
    const params: Record<string, string> = {}
    if (filters.value.start_date) params.start_date = filters.value.start_date
    if (filters.value.end_date) params.end_date = filters.value.end_date

    const response = await apiClient.get('/admin/api/reports/payment-providers', { params })
    let data: ProviderReport[] = response.data.providers

    if (filters.value.provider) {
      data = data.filter((p) => p.payment_method === filters.value.provider)
    }

    providers.value = data
  } catch {
    toast.error(t('admin.payment_reports.toast_load_failed'))
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchReport()
})
</script>

<style scoped>
.payment-reports-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.filter-select,
.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-input[type="date"] {
  width: 150px;
  color-scheme: dark;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-primary, #fff);
}

/* Stat Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  transition: all 150ms ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.stat-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.stat-positive {
  color: var(--color-success, #00e701);
}

.stat-negative {
  color: var(--color-danger, #ed4245);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

.empty-state {
  padding: 48px 20px;
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.provider-name {
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.count-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-weight: 600;
}

.amount-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-weight: 700;
}

.amount-positive {
  color: var(--color-success, #00e701);
}

/* Success Rate */
.success-rate {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
}

.rate-green {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.rate-yellow {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.rate-red {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Processing Time */
.processing-time {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 1024px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .payment-reports-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .filters-row {
    flex-direction: column;
  }

  .stat-grid {
    grid-template-columns: 1fr;
  }
}
</style>
